import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FaCalendarAlt, FaHistory } from "react-icons/fa";
import client from "../api/client";
import "./AttendanceHistory.css";

const todayISO = () => new Date().toISOString().slice(0, 10);
const defaultFromDate = () => {
  const date = new Date();
  date.setDate(date.getDate() - 14);
  return date.toISOString().slice(0, 10);
};

const formatEmployeeName = (employee) =>
  employee?.name ||
  `${employee?.firstName || ""} ${(employee?.lastName || "").trim()}`.trim() ||
  `#${employee?.id || ""}`;

const formatTime = (timeStr) => {
  if (!timeStr) return "—";
  try {
    const [h, m] = timeStr.split(":");
    const d = new Date();
    d.setHours(parseInt(h), parseInt(m));
    return d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });
  } catch {
    return timeStr;
  }
};

const formatHistoryDate = (value) => {
  if (!value) return "â€”";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  // UI improvement: format ISO dates into a readable day-month-year string for the first column.
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
};

export default function AttendanceHistory() {
  const [filters, setFilters] = useState({
    employeeId: "",
    from: defaultFromDate(),
    to: todayISO(),
  });
  const [employees, setEmployees] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  // Fix: guard the mount effect so history auto-loads once without creating duplicate API calls.
  const initialLoadRef = useRef(true);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const res = await client.get("/users");
        setEmployees(res.data || []);
      } catch (err) {
        console.error("fetch employees failed", err);
        setEmployees([]);
      }
    };

    fetchEmployees();
  }, []);

  const loadHistory = useCallback(async () => {
    if (filters.from && filters.to && filters.from > filters.to) {
      setStatus("From date must be before or equal to To date.");
      setHistory([]);
      return;
    }

    setLoading(true);
    setStatus("");
    try {
      const params = {
        from: filters.from,
        to: filters.to,
      };
      if (filters.employeeId) {
        params.employee_id = filters.employeeId;
      }
      const res = await client.get("/attendance/history", { params });
      const rows = res.data || [];
      setHistory(rows);
      if (!rows.length) {
        setStatus("No attendance records found for the selected filters.");
      }
    } catch (err) {
      console.error("attendance history fetch failed", err);
      setStatus("Unable to load attendance history right now.");
      setHistory([]);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    // Fix: restore the original automatic first load while avoiding repeated calls on re-render.
    if (!initialLoadRef.current) return;
    initialLoadRef.current = false;
    loadHistory();
  }, [loadHistory]);

  const tableRows = useMemo(() => {
    return history.map((record) => {
      return {
        ...record,
        workedDisplay: record.worked_hours || "—",
      };
    });
  }, [history]);

  return (
    <div className="attendance-history-page page-container">
      <header className="history-header">
        <div>
          <div className="history-title">
            <FaHistory /> Attendance History
          </div>
          <p className="history-subtitle">
            Filter attendance entries by employee and date span. Works with the new history endpoint.
          </p>
        </div>
      </header>

      <section className="history-card">
        <div className="history-filters">
          <label>
            Employee
            <select
              value={filters.employeeId}
              onChange={(e) => setFilters((prev) => ({ ...prev, employeeId: e.target.value }))}
            >
              <option value="">All employees</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {formatEmployeeName(emp)}
                </option>
              ))}
            </select>
          </label>
          <label>
            From
            <input
              type="date"
              value={filters.from}
              max={filters.to || todayISO()}
              onChange={(e) => setFilters((prev) => ({ ...prev, from: e.target.value }))}
            />
          </label>
          <label>
            To
            <input
              type="date"
              value={filters.to}
              min={filters.from}
              max={todayISO()}
              onChange={(e) => setFilters((prev) => ({ ...prev, to: e.target.value }))}
            />
          </label>
          <button className="btn primary small" onClick={loadHistory} disabled={loading}>
            <FaCalendarAlt />
            {loading ? "Loading…" : "Load History"}
          </button>
        </div>

        {status && (
          <div className="history-status">
            <span>{status}</span>
          </div>
        )}

        <div className="history-table">
          {loading ? (
            <div className="history-loading">Loading attendance records…</div>
          ) : tableRows.length > 0 ? (
            <table>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>In Time</th>
                  <th>Out Time</th>
                  <th>Lunch In</th>
                  <th>Lunch Out</th>
                  <th>Worked Hours</th>
                  <th>Day Type</th>
                  <th>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, idx) => (
                  <tr key={`${row.employee_id}-${row.date}-${row.id || idx}`}>
                    <td>
                      {/* UI improvement: make the employee name the primary text and show the formatted date below it. */}
                      <strong className="history-employee-name">
                        {row.employee_name || row.employee_id}
                      </strong>
                      <div className="history-date-text">{formatHistoryDate(row.date)}</div>
                    </td>
                    <td>{formatTime(row.in_time)}</td>
                    <td>{formatTime(row.out_time)}</td>
                    <td>{formatTime(row.lunch_in)}</td>
                    <td>{formatTime(row.lunch_out)}</td>
                    <td>{row.workedDisplay}</td>
                    <td>{row.day_type || "—"}</td>
                    <td>{row.remarks || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="history-empty">
              <span>No attendance records yet. Adjust the filters and try again.</span>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
